import {
  Button,
  Card,
  Checkbox,
  Container,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  SelectChangeEvent,
  Box,
} from '@mui/material';
import { debounce } from 'lodash';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import PageHeader from 'components/PageHeader/PageHeader';
import Popup from 'components/Popup/Popup';
import MoreMenu from 'components/table/MoreMenu';
import { Link, useNavigate } from 'react-router-dom';
import SearchNotFound from 'components/table/SearchNoFound';
import TableHeader from 'components/table/TableHeader';
import TableToolbar from 'components/table/TableToolbar';
import { getCompobotsTableHead } from 'constans/table-headers';
import SnackbarContext from 'contexts/snackbarContext';
import {
  deleteCompobot,
  deleteCompobotBulk,
  getCompobots,
  searchCompobots,
} from 'services/compobot.service';
import { sortByColumn } from 'services/list.service';
import { CompobotType } from 'types/compobots.type';
import QrCodeDialog from './QrCodeDialog';
import CompobotCard from 'components/CompobotCard/CompobotCard';
import CompobotCardSkeleton from 'components/CompobotCard/CompobotCardSkeleton';
import { Grid, IconButton, PaginationItem } from '@mui/material';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';


export default function CompoBotsPage() {
  const navigate = useNavigate();
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [selected, setSelected] = useState<string[]>([]);
  const [orderBy, setOrderBy] = useState('deviceId');
  const [rowsPerPage, setRowsPerPage] = useState<number>(6);
  const { handleOpen } = useContext(SnackbarContext);
  const [compobotList, setCompobotList] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [selectedCompobot, setSelectedCompobot] = useState<CompobotType | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    getCompobotsHandler();
  }, []);

  const getCompobotsHandler = () => {
    setIsLoading(true);
    getCompobots()
      .then((response) => {
        setCompobotList(response.data);
      })
      .finally(() => setIsLoading(false));
  };

  const searchFilter = useCallback(
    debounce((filterName) => {
      if (filterName) {
        searchCompobots(filterName).then((response) => {
          setCompobotList(response.data);
        });
      } else {
        getCompobotsHandler();
      }
    }, 300),
    [],
  );

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (checked: boolean) => {
    if (checked) {
      const newSelecteds = compobotList.map((n: CompobotType) => n.id);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (name: string) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected: string[] = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }
    setSelected(newSelected);
  };

  // TablePagination uses this handler
  const handlePaginationChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Select (MUI) uses this handler
  const handleSelectChangeRowsPerPage = (
    event: SelectChangeEvent<string>,
  ) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  };

  const handleDeleteCompobot = (id: string) => {
    deleteCompobot(id)
      .then(() => {
        handleSelectAllClick(false);
        setOpenModal(false);
        setCompobotList((list) =>
          list.filter((compobot: CompobotType) => compobot.id !== id),
        );
        handleOpen(t('compobotsPage.deleteSuccessMessage'), 'success');
      })
      .catch(({ data }) => {
        handleOpen(data.message);
      });
  };

  const handleDeleteCompobotBulk = (ids: string[]) => {
    deleteCompobotBulk(ids)
      .then(() => {
        setCompobotList(compobotList.filter(({ id }) => !ids.includes(id)));
        handleSelectAllClick(false);
        setOpenModal(false);
        handleOpen(t('compobotsPage.bulkDeleteSuccessMessage'), 'success');
      })
      .catch(({ data }) => {
        handleOpen(data.message);
      });
  };

  const deleteCompobotAction = () => {
    if (selected.length > 1) {
      handleDeleteCompobotBulk(selected);
    } else {
      handleDeleteCompobot(selected[0]);
    }
  };

  const openModalHandler = (id?: string) => {
    setOpenModal(true);
    if (id) {
      setSelected([id]);
    }
  };

  const sortedCompobots = [...compobotList].sort((a, b) => {
    const getValue = (item: CompobotType) => {
      if (orderBy === 'score') return item.organisation?.score ?? 0;
      if (orderBy === 'lastInsertTime') return item.lastInsertTime ?? '';
      if (orderBy === 'isOnline') return item.isOnline ? 1 : 0;
      if (orderBy === 'organisation') return item.organisation?.companyName ?? '';
      if (orderBy === 'temperature') return item.temperature ?? 0;
      if (orderBy === 'humidity') return item.humidity ?? 0;
      return item[orderBy as keyof CompobotType] ?? '';
    };

    const valA = getValue(a);
    const valB = getValue(b);

    if (valA < valB) return order === 'asc' ? -1 : 1;
    if (valA > valB) return order === 'asc' ? 1 : -1;
    return 0;
  });

  const compobotsTableHead = useMemo(getCompobotsTableHead, [language]);

  /*
  const goToGrafana = (deviceId?: string, page?: string) => {
    if (page === 'logs') {
      const dashboardUrl = `https://prod.compocity.help/grafana/d/62c4c21a-369c-4977-b961-ab1fa3bb03d6/logsfromonebot?var-compobotID=${deviceId}&orgId=1&from=now/d&to=now&timezone=browser`;
      window.location.href = dashboardUrl;
    } else {
      const dashboardUrl = `https://prod.compocity.help/grafana/d/b30df3dd-92b3-4b09-bfeb-281bd919756f/telemetryfromonebot?var-compobotID=${deviceId}&orgId=1&from=now%2Fd&to=now&timezone=browser`;
      window.location.href = dashboardUrl;
    }
  };
   */

  const goToGrafana = (deviceId?: string, page?: string) => {
    if (!deviceId) return;

    const baseUrl =
      page === 'logs'
        ? 'https://prod.compocity.help/grafana/d/62c4c21a-369c-4977-b961-ab1fa3bb03d6/logsfromonebot'
        : 'https://prod.compocity.help/grafana/d/b30df3dd-92b3-4b09-bfeb-281bd919756f/telemetryfromonebot';

    const dashboardUrl = `${baseUrl}?var-compobotID=${deviceId}&orgId=1&from=now/d&to=now&timezone=browser`;

    window.open(dashboardUrl, '_blank', 'noopener,noreferrer');
  };

  const [viewMode, setViewMode] = useState<'table' | 'cards'>('cards');

  const goToLogsPage = (compobotDbId: string) => {
    navigate(`/compobot/${compobotDbId}/logs`);
  };

  return (


    <Container>

      <PageHeader
        title={t('compobotsPage.title')}
        buttonTitle={t('compobotsPage.buttonTitle').toString()}
        hasButton
        onButtonClick={() => navigate('/compobots/new')}
        rightSlot={
          <IconButton onClick={() => setViewMode(viewMode === 'table' ? 'cards' : 'table')}>
            {viewMode === 'table' ? <ViewModuleIcon /> : <ViewListIcon />}
          </IconButton>
        }
      />


      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mt={3} mb={3} alignItems="center" justifyContent="space-between" sx={{ minHeight: 56 }}>

        <TextField
          label="Search..."
          variant="outlined"
          size="small"
          onChange={(e) => searchFilter(e.target.value)}
          sx={{ minWidth: 200 }}
        />

        <Stack direction="row" spacing={1} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="sort-label">Rendezés</InputLabel>
            <Select
              labelId="sort-label"
              value={orderBy}
              label="Rendezés"
              onChange={(e) => setOrderBy(e.target.value)}
            >
              <MenuItem value="deviceId">Név</MenuItem>
              <MenuItem value="score">Pontszám</MenuItem>
              <MenuItem value="lastInsertTime">Utolsó bedobás</MenuItem>
              <MenuItem value="organisation">Szervezet</MenuItem>
              <MenuItem value="isOnline">Online állapot</MenuItem>
              <MenuItem value="temperature">Hőmérséklet</MenuItem>
              <MenuItem value="humidity">Páratartalom</MenuItem>
            </Select>
          </FormControl>

          <Button
            onClick={() => setOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))}>{order === 'asc' ? '⬆️' : '⬇️'}</Button>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="per-page-label">Page size</InputLabel>
            <Select
              labelId="per-page-label"
              value={rowsPerPage.toString()}
              label="Page size"
              onChange={handleSelectChangeRowsPerPage}
            >
              <MenuItem value="6">{`6 / ${compobotList.length}`}</MenuItem>
              <MenuItem value="12">{`12 / ${compobotList.length}`}</MenuItem>
              <MenuItem value="18">{`18 / ${compobotList.length}`}</MenuItem>
            </Select>
          </FormControl>

        </Stack>

        <Pagination
          count={Math.ceil(compobotList.length / rowsPerPage)}
          page={page + 1}
          onChange={(_, value) => setPage(value - 1)}
          color="primary"
          sx={{ width: 400, justifyContent: 'center', display: 'flex' }} // <-- ez a kulcs
          renderItem={(item) => (
            <PaginationItem
              {...item}
              sx={{
                minWidth: 36,
                width: 36,
                textAlign: 'center',
                fontVariantNumeric: 'tabular-nums',
              }}
            />
          )}
        />

      </Stack>


      {viewMode === 'table' ? (
        <Card>
          <Box sx={{ mt: 1 }} />
          <TableContainer>
            <Table>
              <TableHeader
                order={order}
                orderBy={orderBy}
                headLabel={compobotsTableHead}
                rowCount={compobotList.length}
                numSelected={selected.length}
                onRequestSort={handleRequestSort}
                onSelectAllClick={handleSelectAllClick}
              />
              <TableBody>
                {sortedCompobots
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => {
                    const { id, deviceId, organisation }: CompobotType = row;
                    const isItemSelected = selected.indexOf(id) !== -1;

                    return (
                      <TableRow
                        hover
                        key={id}
                        tabIndex={-1}
                        role="checkbox"
                        selected={isItemSelected}
                        aria-checked={isItemSelected}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isItemSelected}
                            onClick={() => handleClick(id)}
                          />
                        </TableCell>
                        <TableCell component="th" scope="row" padding="none">
                          <Stack direction="row" alignItems="center" spacing={2}>
                            <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600 }}>
                              {deviceId}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell align="left">
                          {organisation?.companyName}
                        </TableCell>
                        <TableCell align="left">{organisation?.score}</TableCell>

                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <MoreMenu onDelete={() => openModalHandler(id)} id={id} />
                            <Button variant="outlined" size="small" onClick={() => goToGrafana(deviceId, 'logs')}>Logs</Button>
                            <Button variant="outlined" size="small" onClick={() => goToGrafana(deviceId, 'telemetry')}>Telemetry</Button>
                            <Button variant="outlined" size="small" onClick={() => {setSelectedCompobot(row);setQrDialogOpen(true);}}>QR</Button>
                            <Button variant="contained" size="small" onClick={() => goToLogsPage(id)}>Logok</Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
              {!compobotList.length && (
                <TableBody>
                  <TableRow>
                    <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                      <SearchNotFound />
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </TableContainer>

        </Card>
      ) : (
        <Grid container spacing={2}>
          {isLoading
            ? Array.from({ length: rowsPerPage }).map((_, idx) => (
              <Grid item xs={12} sm={6} md={4} lg={4} key={idx} sx={{ mb: 2 }}>
                <CompobotCardSkeleton />
              </Grid>
            ))
            : sortedCompobots
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row: CompobotType) => (
                <Grid item xs={12} sm={6} md={4} lg={4} key={row.id} sx={{ mb: 2 }}>
                  <CompobotCard
                    compobot={row}
                    onOpenQr={() => {
                      setSelectedCompobot(row);
                      setQrDialogOpen(true);
                    }}
                    goToGrafana={goToGrafana}
                    onViewLogs={() => navigate(`/compobot/${row.id}/logs`)}
                  />
                </Grid>
              ))}
        </Grid>
      )}


      <Popup
        type="delete"
        open={openModal}
        onConfirm={deleteCompobotAction}
        handleClose={() => setOpenModal(false)}
      />
      <QrCodeDialog
        open={qrDialogOpen}
        onClose={() => setQrDialogOpen(false)}
        compobot={selectedCompobot}
      />
    </Container>
  );
}
